import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ConversationHistoryEntry,
  ConversationHistoryMix,
  ConversationHistoryParticipant,
  ConversationMixGenderValue,
} from '@digital-twins/shared-types'
import { formatDistanceToNow } from 'date-fns'
import { MessageCircle, MoreVertical, Pencil, Trash2 } from '@digital-twins/design-system/icons'
import { SubsegmentChip, SubsegmentOverflowChip } from '@/components/twin-chat'
import { CountryFlag } from '@/components/ui/CountryFlag'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DeleteStudyDialog } from '@/features/studies/list/page/components/DeleteStudyDialog'
import { cn } from '@/lib/utils'

interface ConversationCardProps {
  entry: ConversationHistoryEntry;
  onClick: () => void;
  onDelete?: (id: string) => void;
  onRename?: (id: string, title: string) => void;
  /** Controls delete confirmation copy — quick-study lobby vs conversations lobby. */
  deleteMode?: 'conversation' | 'quick-study';
}

function getInitials(title: string): string {
  return title
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return ''
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
  } catch {
    return ''
  }
}

const COMPACT_TIME_UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
  { unit: 'year', seconds: 31536000 },
  { unit: 'month', seconds: 2592000 },
  { unit: 'week', seconds: 604800 },
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
]

/** Short relative time ("10h ago") for the phone title line, where every character counts. */
function formatCompactRelativeTime(dateStr: string | null, locale: string): string {
  if (!dateStr) return ''
  const elapsedSeconds = (Date.now() - new Date(dateStr).getTime()) / 1000
  if (!Number.isFinite(elapsedSeconds)) return ''
  try {
    const format = new Intl.RelativeTimeFormat(locale, { style: 'narrow', numeric: 'always' })
    const match = COMPACT_TIME_UNITS.find((entry) => elapsedSeconds >= entry.seconds)
    if (!match) {
      return format.format(0, 'minute')
    }
    return format.format(-Math.floor(elapsedSeconds / match.seconds), match.unit)
  } catch {
    return ''
  }
}

const VISIBLE_FACES = 3
const VISIBLE_SUBSEGMENTS = 2
const VISIBLE_FLAGS = 3

/** The participants to draw: the typed list, else bare photos from an older API. */
function cardParticipants(entry: ConversationHistoryEntry): Array<ConversationHistoryParticipant> {
  if (Array.isArray(entry.participants) && entry.participants.length > 0) {
    return entry.participants
  }
  return entry.participantImages.map((imageUrl) => ({ name: '', imageUrl, tagline: null }))
}

interface FaceProps {
  participant: ConversationHistoryParticipant;
  fallbackLabel: string;
  className: string;
  testId?: string;
}

function Face(props: FaceProps) {
  const { participant, fallbackLabel, className, testId } = props

  const [hasImageError, setHasImageError] = useState(false)

  const initials = getInitials(participant.name || fallbackLabel)

  if (!participant.imageUrl || hasImageError) {
    return (
      <span
        className={cn('flex items-center justify-center bg-muted text-xs font-medium text-muted-foreground', className)}
        aria-hidden
        data-testid={testId}
      >
        {initials}
      </span>
    )
  }
  return (
    <img
      src={participant.imageUrl}
      alt=""
      title={participant.name || undefined}
      className={cn('bg-muted object-cover', className)}
      data-testid={testId}
      onError={() => setHasImageError(true)}
    />
  )
}

interface FacesRowProps {
  participants: Array<ConversationHistoryParticipant>;
  total: number;
  fallbackLabel: string;
}

function FacesRow(props: FacesRowProps) {
  const { participants, total, fallbackLabel } = props

  const shown = participants.slice(0, VISIBLE_FACES)
  const overflow = Math.max(0, total - shown.length)

  return (
    <div className="flex shrink-0 items-center" data-testid="conversation-card-faces">
      {shown.map((participant, index) => (
        <Face
          key={`${participant.name}-${participant.imageUrl ?? ''}-${index}`}
          participant={participant}
          fallbackLabel={fallbackLabel}
          testId={`conversation-card-face-${index}`}
          className={cn('h-8 w-8 rounded-full ring-2 ring-background', index > 0 && '-ml-2')}
        />
      ))}
      {overflow > 0 && (
        <span
          className="-ml-2 flex h-8 min-w-8 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground ring-2 ring-background"
          data-testid="conversation-card-faces-overflow"
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}

interface MixLabels {
  women: (count: number) => string;
  men: (count: number) => string;
  nonbinary: (count: number) => string;
  ages: (from: string, to: string) => string;
  agesOpen: (from: string) => string;
  agesUnder: (from: string) => string;
  agesUnderTo: (from: string, to: string) => string;
}

const GENDER_LABEL_KEYS: Record<ConversationMixGenderValue, 'women' | 'men' | 'nonbinary'> = {
  female: 'women',
  male: 'men',
  nonbinary: 'nonbinary',
}

/** The age span: '<18' marks an open lower range ('Under 18'), '65+' an open upper one. */
function agesText(ageMin: string | null, ageMax: string | null, labels: MixLabels): string {
  if (!ageMin || !ageMax) {
    return ''
  }
  if (ageMin.startsWith('<')) {
    const under = ageMin.slice(1)
    return ageMax === ageMin ? labels.agesUnder(under) : labels.agesUnderTo(under, ageMax)
  }
  const isOpenOnly = ageMax.endsWith('+') && ageMax.slice(0, -1) === ageMin
  return isOpenOnly ? labels.agesOpen(ageMin) : labels.ages(ageMin, ageMax)
}

/**
 * B2C: "4 women, 2 men · ages 25 to 54". B2B: the professional facets,
 * "Director +2 · IT +1". '' when the mix holds nothing to say.
 */
function mixText(mix: ConversationHistoryMix | undefined, labels: MixLabels): string {
  if (!mix) {
    return ''
  }
  const facets = (mix.facets ?? []).map((facet) => (facet.others > 0 ? `${facet.top} +${facet.others}` : facet.top))
  const genders = (mix.genders ?? [])
    .filter((gender) => gender.count > 0 && GENDER_LABEL_KEYS[gender.value])
    .map((gender) => labels[GENDER_LABEL_KEYS[gender.value]](gender.count))
    .join(', ')
  const ages = agesText(mix.ageMin, mix.ageMax, labels)

  return [...facets, genders, ages].filter(Boolean).join(' \u00b7 ')
}

function countryName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

export function ConversationCard(props: ConversationCardProps) {
  const { entry, onClick, onDelete, onRename, deleteMode = 'conversation' } = props

  const { t, i18n } = useTranslation('app')
  const relativeTime = formatRelativeTime(entry.lastMessageAt)
  const compactRelativeTime = formatCompactRelativeTime(entry.lastMessageAt, i18n.language)

  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(entry.title)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Guards against a second commit when Enter commits and the subsequent
  // unmount fires a trailing blur (both would otherwise call onRename).
  const settledRef = useRef(false)
  // The phone menu runs its action once it has closed, so its focus restore
  // cannot blur the rename input or race the delete dialog.
  const pendingMenuActionRef = useRef<'rename' | 'delete' | null>(null)

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDeleteDialogOpen(true)
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
  }

  const handleConfirmDelete = async () => {
    if (!onDelete) {
      return
    }
    setDeleting(true)
    try {
      await Promise.resolve(onDelete(entry.id))
      setDeleteDialogOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  const startRename = (e: React.MouseEvent) => {
    e.stopPropagation()
    settledRef.current = false
    setDraft(entry.title)
    setIsEditing(true)
  }

  const cancelRename = () => {
    settledRef.current = true
    setIsEditing(false)
    setDraft(entry.title)
  }

  const commitRename = () => {
    if (!isEditing || settledRef.current) return
    settledRef.current = true
    const next = draft.trim()
    setIsEditing(false)
    if (next && next !== entry.title) {
      onRename?.(entry.id, next)
    } else {
      setDraft(entry.title)
    }
  }

  const onMenuCloseAutoFocus = (event: Event) => {
    const action = pendingMenuActionRef.current
    pendingMenuActionRef.current = null
    if (!action) {
      return
    }
    event.preventDefault()
    if (action === 'rename') {
      settledRef.current = false
      setDraft(entry.title)
      setIsEditing(true)
    } else {
      setDeleteDialogOpen(true)
    }
  }

  // Below sm the hover-only actions cannot be reached by touch: one always
  // visible menu on the title line holds them instead.
  const mobileMenu = (onRename || onDelete) && !isEditing && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label={t('conversations.cardActions')}
          onClick={(e) => e.stopPropagation()}
          data-testid={`conversation-card-menu-${entry.id}`}
          className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground sm:hidden"
        >
          <MoreVertical className="h-4 w-4" strokeWidth={1.5} />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
        onCloseAutoFocus={onMenuCloseAutoFocus}
      >
        {onRename && (
          <DropdownMenuItem
            onSelect={() => {
              pendingMenuActionRef.current = 'rename'
            }}
            data-testid={`conversation-card-menu-rename-${entry.id}`}
          >
            <Pencil className="mr-2 h-4 w-4" strokeWidth={1.5} />
            {t('conversations.rename')}
          </DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem
            onSelect={() => {
              pendingMenuActionRef.current = 'delete'
            }}
            data-testid={`conversation-card-menu-delete-${entry.id}`}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} />
            {t('delete', { ns: 'common' })}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const renameButton = onRename && !isEditing && (
    <span
      role="button"
      tabIndex={-1}
      onClick={startRename}
      data-testid={`conversation-card-rename-${entry.id}`}
      className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
    >
      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
    </span>
  )

  const participants = cardParticipants(entry)
  const isOneOnOne = entry.type === 'one_on_one'
  const oneOnOneTagline = isOneOnOne
    ? [participants[0]?.name?.trim(), participants[0]?.tagline?.trim()]
        .filter((part) => Boolean(part) && part !== entry.title.trim())
        .join(' · ')
    : ''
  const mixLine = isOneOnOne
    ? ''
    : mixText(entry.mix, {
        women: (count) => t('conversations.card.women', { count }),
        men: (count) => t('conversations.card.men', { count }),
        nonbinary: (count) => t('conversations.card.nonbinary', { count }),
        ages: (from, to) => t('conversations.card.ages', { from, to }),
        agesOpen: (from) => t('conversations.card.agesOpen', { from }),
        agesUnder: (from) => t('conversations.card.agesUnder', { from }),
        agesUnderTo: (from, to) => t('conversations.card.agesUnderTo', { from, to }),
      })
  const countries = isOneOnOne ? [] : (entry.mix?.countries ?? [])
  const shownCountries = countries.slice(0, VISIBLE_FLAGS)
  const hiddenCountries = countries.length - shownCountries.length
  const subsegments = isOneOnOne ? [] : (entry.subsegments ?? [])
  const shownSubsegments = subsegments.slice(0, VISIBLE_SUBSEGMENTS)
  const hiddenSubsegments = subsegments.length - shownSubsegments.length

  const titleNode = isEditing ? (
    <input
      ref={inputRef}
      value={draft}
      maxLength={200}
      onChange={(e) => setDraft(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Enter') {
          e.preventDefault()
          commitRename()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          cancelRename()
        }
      }}
      onBlur={commitRename}
      data-testid={`conversation-card-rename-input-${entry.id}`}
      className="min-w-0 flex-1 rounded border border-border bg-background px-1.5 py-0.5 text-lg font-semibold leading-snug text-foreground outline-none focus:ring-1 focus:ring-ring"
    />
  ) : (
    <span className="min-w-0 line-clamp-2 text-lg font-semibold leading-snug text-foreground">
      {entry.title}
    </span>
  )

  const desktopActions = (renameButton || onDelete) && !isEditing && (
    <div className="ml-auto hidden items-center gap-1 sm:flex">
      {renameButton}
      {onDelete && (
        <span
          role="button"
          tabIndex={-1}
          onClick={handleDelete}
          data-testid={`conversation-card-delete-${entry.id}`}
          className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        </span>
      )}
    </div>
  )

  return (
    <>
    <button
      type="button"
      onClick={onClick}
      data-testid={`conversation-card-${entry.id}`}
      className="group relative flex w-full flex-col gap-4 rounded-xl border border-border bg-background p-5 text-left transition-colors hover:border-foreground/20 sm:px-6"
    >
      <div className="flex w-full items-start gap-3">
        {isOneOnOne && (
          <Face
            participant={participants[0] ?? { name: entry.title, imageUrl: null, tagline: null }}
            fallbackLabel={entry.title}
            testId={`conversation-card-portrait-${entry.id}`}
            className="h-14 w-14 shrink-0 rounded-xl text-sm"
          />
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            {titleNode}
            {compactRelativeTime && (
              <span
                title={relativeTime}
                className="ml-auto shrink-0 whitespace-nowrap text-xs text-muted-foreground sm:hidden"
                data-testid={`conversation-card-time-${entry.id}`}
              >
                {compactRelativeTime}
              </span>
            )}
            {mobileMenu}
            {desktopActions}
            {relativeTime && (
              <span
                className={cn(
                  'hidden shrink-0 whitespace-nowrap text-xs text-muted-foreground sm:inline',
                  !desktopActions && 'ml-auto',
                )}
              >
                {relativeTime}
              </span>
            )}
          </div>

          {!isEditing && entry.subtitle && (
            <span className="truncate text-sm text-muted-foreground" data-testid={`conversation-card-audience-${entry.id}`}>
              {entry.subtitle}
            </span>
          )}

          {isOneOnOne && oneOnOneTagline && (
            <p className="line-clamp-1 text-sm text-muted-foreground" data-testid={`conversation-card-tagline-${entry.id}`}>
              {oneOnOneTagline}
            </p>
          )}
        </div>
      </div>

      {entry.lastMessagePreview && (
        <p className="line-clamp-3 text-base leading-relaxed text-foreground" data-testid={`conversation-card-preview-${entry.id}`}>
          &ldquo;{entry.lastMessagePreview}&rdquo;
        </p>
      )}

      <div
        className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground"
        data-testid={`conversation-card-stats-${entry.id}`}
      >
        {!isOneOnOne && (
          <div className="flex min-w-0 items-center gap-2">
            <FacesRow
              participants={participants.length > 0 ? participants : [{ name: entry.title, imageUrl: null, tagline: null }]}
              total={Math.max(entry.participantCount, 1)}
              fallbackLabel={entry.title}
            />
            <span className="whitespace-nowrap" data-testid={`conversation-card-twin-count-${entry.id}`}>
              {t('conversations.card.twins', { count: entry.participantCount })}
            </span>
          </div>
        )}
        {(mixLine || shownCountries.length > 0) && (
          <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1" data-testid={`conversation-card-mix-${entry.id}`}>
            {mixLine && <span>{mixLine}</span>}
            {shownCountries.length > 0 && (
              <span className="inline-flex items-center gap-1">
                {shownCountries.map((code, index) => (
                  <CountryFlag
                    key={code}
                    code={code}
                    title={countryName(code, i18n.language)}
                    data-testid={`conversation-card-flag-${entry.id}-${index}`}
                  />
                ))}
                {hiddenCountries > 0 && (
                  <span className="text-xs" data-testid={`conversation-card-flags-overflow-${entry.id}`}>
                    +{hiddenCountries}
                  </span>
                )}
              </span>
            )}
          </span>
        )}
        {shownSubsegments.length > 0 && (
          <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
            {shownSubsegments.map((subsegment, index) => (
              <SubsegmentChip
                key={subsegment.name}
                label={`${subsegment.name} \u00b7 ${subsegment.count}`}
                className="max-w-full truncate"
                testId={`conversation-card-subsegment-${entry.id}-${index}`}
              />
            ))}
            {hiddenSubsegments > 0 && (
              <SubsegmentOverflowChip
                count={hiddenSubsegments}
                testId={`conversation-card-subsegment-overflow-${entry.id}`}
              />
            )}
          </span>
        )}
        {entry.messageCount > 0 && (
          <span
            className="ml-auto inline-flex items-center gap-1.5 whitespace-nowrap"
            data-testid={`conversation-card-message-count-${entry.id}`}
          >
            <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
            {t('conversations.card.messages', { count: entry.messageCount })}
          </span>
        )}
      </div>
    </button>

    {onDelete && (
      <DeleteStudyDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        studyName={entry.title}
        mode={deleteMode}
        deleting={deleting}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    )}
    </>
  )
}
